import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { tiposTarefaApi } from '../../features/cadastros-gerais/tipos-tarefa/api';
import type { TipoTarefaListItem } from '../../features/cadastros-gerais/tipos-tarefa/types';

export interface TipoTarefaSelectProps {
  value: number | null;
  onChange: (id: number | null, item: TipoTarefaListItem | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

export function TipoTarefaSelect({
  value,
  onChange,
  label,
  error,
  helperText,
  disabled,
}: TipoTarefaSelectProps): JSX.Element {
  const { t } = useTranslation();

  const list = useQuery({
    queryKey: ['task-types', 'select-all'],
    queryFn: () =>
      tiposTarefaApi.list({
        page: 1,
        pageSize: 200,
        sortBy: 'nome',
        sortOrder: 'asc',
      }),
    staleTime: 5 * 60 * 1000,
  });

  const options: TipoTarefaListItem[] = (list.data?.data ?? []).filter((o) => o.ativo);
  const selected: TipoTarefaListItem | null = options.find((o) => o.id === value) ?? null;

  return (
    <Autocomplete<TipoTarefaListItem>
      value={selected}
      onChange={(_, v) => onChange(v?.id ?? null, v)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(o) => o.nome}
      options={options}
      loading={list.isFetching}
      disabled={disabled}
      noOptionsText={t('components.asyncSelect.noOptions')}
      loadingText={t('components.asyncSelect.loading')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('components.tipoTarefaSelect.label')}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {list.isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
